import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { useEffect, useState } from "react";
import { push, ref, update } from "firebase/database";
import { IRoom } from "../@types/room";
import { db } from "../firebase";
import { useFirebase } from "../FbContex";
import { allRooms, nextOrder } from "../lib/selectors";
import { prune } from "../lib/node";
import { slugify } from "../lib/slug";
import { TextField, NumberField, SwitchField } from "./form/Fields";
import { DIALOG_BACKDROP, DIALOG_PANEL } from "./tableStyles";

type Param = {
  room: IRoom | null;
  close: () => void;
};

export default function RoomForm({ room, close }: Param) {
  const { rooms } = useFirebase();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [icon, setIcon] = useState("");
  const [order, setOrder] = useState<number | undefined>();
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!room) return;
    setName(room.name || "");
    setSlug(room.slug || "");
    setIcon(room.icon || "");
    setOrder(room.order ?? nextOrder(allRooms(rooms)));
    setDisabled(room.disabled ?? false);
    setError("");
    // Only reset when a different room is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  const effectiveSlug = slug.trim() || slugify(name);

  const save = () => {
    if (!name.trim()) return setError("Name is required.");
    if (!effectiveSlug) return setError("Slug is required.");

    const clash = allRooms(rooms).find(
      (r) => r.slug === effectiveSlug && r.id !== room?.id
    );
    if (clash) return setError(`Slug "${effectiveSlug}" is already used by ${clash.name}.`);

    setError("");
    setLoading(true);
    const data = prune({
      name: name.trim(),
      slug: effectiveSlug,
      icon: icon.trim() || undefined,
      order: order ?? nextOrder(allRooms(rooms)),
      disabled,
    });

    // update() rather than set(): it leaves alone any field a later version adds.
    (room?.id
      ? update(ref(db, `rooms/${room.id}`), data)
      : push(ref(db, "rooms"), data)
    )
      .then(close, (err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  return (
    <Dialog open={!!room} onClose={close} className="relative z-50">
      <DialogBackdrop transition className={DIALOG_BACKDROP} />
      <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
        <div className="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
          <DialogPanel transition className={DIALOG_PANEL}>
            <div className="py-4 flex justify-between text-xl items-center">
              <h3 className="font-semibold opacity-70">
                {room?.id ? "Edit" : "Add"} Room
              </h3>
              <button className="text-red-400" onClick={close}>
                <i className="fa-solid fa-xmark" />
              </button>
            </div>

            <div className="p-5 border-y border-gray-500/50 flex-col space-y-5">
              <TextField label="Name" value={name} onChange={setName} />
              <TextField
                label="Slug"
                value={slug}
                onChange={setSlug}
                placeholder={slugify(name)}
                hint={`URL will be /room/${effectiveSlug || "..."}`}
              />
              <TextField
                label="Icon"
                value={icon}
                onChange={setIcon}
                hint="Font Awesome name, e.g. couch"
              />
              <NumberField label="Order" value={order} onChange={setOrder} />
              <SwitchField
                label="Disabled"
                checked={disabled}
                onChange={setDisabled}
                hint="Hidden from the dashboard."
              />
              {error && <div className="text-sm text-red-400">{error}</div>}
            </div>

            <div className="px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                disabled={loading}
                type="button"
                onClick={save}
                className="inline-flex w-full justify-center rounded-md bg-indigo-500 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-400 sm:ml-3 sm:w-32 disabled:opacity-40"
              >
                {loading ? "..." : "Save"}
              </button>
              <button
                type="button"
                data-autofocus
                onClick={close}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-slate-500/20 px-3 py-2 text-sm font-semibold shadow-sm ring-1 ring-inset ring-slate-500/40 hover:bg-slate-500/30 sm:mt-0 sm:w-auto"
              >
                Cancel
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
